import * as Blockly from 'blockly'
import { LuaGenerator } from 'blockly/lua'
import { messageExtensionName, messageFieldName } from './messages'
import {
  missionObjectExtensionName,
  missionObjectFieldName,
  missionObjectOrThisFieldName,
} from './constants'

interface BlockArg {
  check?: string
  name: string
  precision?: number
  type: string
  value?: number | string
  variable?: string
  min?: number
  max?: number
}

export interface BlockDefinition {
  args0?: BlockArg[]
  args1?: BlockArg[]
  args2?: BlockArg[]
  args3?: BlockArg[]
  extensions?: string[]
  generator: (
    block: Blockly.Block,
    generator: LuaGenerator,
  ) => string | [string, number] | null
  icon?: new (block: Blockly.Block) => Blockly.icons.Icon
  message0: string
  message1?: string
  message2?: string
  message3?: string
  opcode: string
  previousStatement?: string | null
  style?: string
}

export const binaryBooleanOperator = {
  args0: [
    { type: 'input_value', name: 'VAL', check: 'Boolean' },
    { type: 'input_value', name: 'VAL2', check: 'Boolean' },
  ],
  inputsInline: true,
  output: 'Boolean',
}

export const binaryNumberOperator = {
  args0: [
    { type: 'input_value', name: 'VAL', check: 'Number' },
    { type: 'input_value', name: 'VAL2', check: 'Number' },
  ],
  inputsInline: true,
  output: 'Number',
}

export const binaryOperator = {
  args0: [
    { type: 'input_value', name: 'VAL' },
    { type: 'input_value', name: 'VAL2' },
  ],
  inputsInline: true,
  output: 'String',
}

export const binaryStringOperator = {
  args0: [
    { type: 'input_value', name: 'VAL', check: 'String' },
    { type: 'input_value', name: 'VAL2', check: 'String' },
  ],
  inputsInline: true,
  output: 'String',
}

export const booleanOutput = {
  output: 'Boolean',
}

export const equalityOperator = {
  ...booleanOutput,
  args0: [
    { type: 'input_value', name: 'VAL' },
    { type: 'input_value', name: 'VAL2' },
  ],
  inputsInline: true,
}

export const genericInput = {
  args0: [{ type: 'input_value', name: 'VAL' }],
}

export const indent = (code: string, levels = 1) =>
  code
    .split('\n')
    .map((line) => (line ? '  '.repeat(levels) + line : line))
    .join('\n')

export const inputHasChildType = (
  block: Blockly.Block,
  name: string,
  type: string,
) => {
  let child = block.getInputTargetBlock(name)
  while (child) {
    if (child.type === type) {
      return true
    }
    child = child.getNextBlock()
  }
  return false
}

export const makeDropdownField = (options: Blockly.MenuOption[]) => ({
  args0: [
    {
      type: 'field_dropdown',
      name: 'VAL',
      options,
    },
  ],
})

export const makeMissionObjectField = (includeThis = false) => ({
  args0: [
    {
      type: 'input_dummy',
      name: includeThis ? missionObjectOrThisFieldName : missionObjectFieldName,
    },
  ],
  extensions: [missionObjectExtensionName],
  inputsInline: true,
})

export const messageField = {
  args0: [{ type: 'input_dummy', name: messageFieldName }],
  extensions: [messageExtensionName],
  inputsInline: true,
}

export const numberInput = {
  args0: [{ type: 'input_value', name: 'VAL', check: 'Number' }],
}

export const numberOutput = {
  output: 'Number',
}

export const relationalOperator = {
  ...binaryNumberOperator,
  ...booleanOutput,
}

export const statementBlock = {
  nextStatement: null,
  previousStatement: null,
}

export const stringInput = {
  args0: [{ type: 'input_value', name: 'VAL', check: 'String' }],
}

export const substackInput = {
  args1: [{ type: 'input_statement', name: 'SUBSTACK' }],
}

export const triggerBlock = {
  nextStatement: null,
}

export const untypedOutput = {
  output: null,
}
